import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { createContext, ReactNode, useContext, useState } from "react";
import { apiModulo, apiWhatsApp } from "../services/api";
import { useModal } from "../hooks/ModalContext";

interface FilaContextProviderProps {
  children: ReactNode;
}

export interface QueueProps {
  id_queue: string;
  name: string;
  cpf_cnpj: string;
  contact: string;
  priority: string;
  id_operator: number;
  id_origin_credor: number;
  date_origin: string;
  date_start_service?: string;
  date_assignment?: string;
  date_end_service?: string;
  id_channel?: number;
  id_reason?: number;
  id_status?: number;
  id_chat?: string;
  reason: {
    id_reason: number;
    desc_reason: string;
  };
  channel: {
    id_channel: number;
    channel: string;
  };
  status: {
    id_status: number;
    desc_status: string;
  };
  credor?: {
    id_credor: number;
    name_credor: string;
    credor: string;
    credor_crm: number;
  };
}

interface FilaContextProps {
  devedorFilaSelected: QueueProps;
  fila: QueueProps[];
  getDevedorFilaSelected(devedor: QueueProps): void;
  endQueue(id_queue: string): Promise<void>;
  getChatId(id_queue: number, id_chat: string): Promise<void>;
  clearSelectedFila(): void;
  getQueue(data: QueueProps[]): void;
}

const FilaContext = createContext<FilaContextProps>({} as FilaContextProps);

export const FilaProvider = ({ children }: FilaContextProviderProps) => {
  const queryClient = useQueryClient();
  const [devedorFilaSelected, setDevedorFilaSelected] = useState<QueueProps>(
    {} as QueueProps
  );
  const [fila, setFila] = useState<QueueProps[]>([]);
  const { setModal } = useModal();

  const mutateFinalizados = useMutation({
    mutationFn: (id_queue: string) => apiModulo.put(`queues/end/${id_queue}`),
    onSuccess: () => {
      setModal("Atendimento finalizado com sucesso!", "Sucesso", true, false);
      queryClient.refetchQueries({ queryKey: ["atendimento"] });
    },
    onError: () =>
      setModal("Ocorreu um erro. Tente novamente!", "Sucesso", false, true),
    mutationKey: ["form"],
  });

  const mutateChatQueue = useMutation({
    mutationFn: (data: { id_queue: number; id_chat: string }) =>
      apiModulo.put(
        "/queues/chat",
        {},
        {
          params: {
            id_queue: data.id_queue,
            id_chat: data.id_chat,
          },
        }
      ),
    mutationKey: ["chat"],
  });

  async function getChatId(id_queue: number, id_chat: string) {
    await mutateChatQueue.mutateAsync({ id_chat, id_queue });
  }

  function getQueue(data: QueueProps[]) {
    setFila(data);
  }

  function getDevedorFilaSelected(devedor: QueueProps) {
    setDevedorFilaSelected(devedor);
  }

  function clearSelectedFila() {
    setDevedorFilaSelected({} as QueueProps);
  }

  async function endQueue(id_queue: string) {
    await mutateFinalizados.mutateAsync(id_queue);
  }

  return (
    <FilaContext.Provider
      value={{
        devedorFilaSelected,
        getDevedorFilaSelected,
        clearSelectedFila,
        endQueue,
        getChatId,
        fila,
        getQueue,
      }}
    >
      {children}
    </FilaContext.Provider>
  );
};

export const useFila = () => useContext(FilaContext);
